import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Search, Plus, FlaskConical, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";

interface LaboratorioResumen {
  id: string;
  razonSocial: string;
  nombreCorto: string | null;
  rfc: string | null;
  acreditacionEma: string | null;
  acreditacionVigenciaFin: string | null;
  contactoCorreo: string | null;
  pais: string;
  estatus: string;
  normasAsignadas: number;
}

interface PageResp<T> {
  content: T[];
  totalElements: number;
}

const ESTATUS_OPCIONES = [
  { value: "", label: "Todos los estatus" },
  { value: "ACTIVO", label: "Activos" },
  { value: "SUSPENDIDO", label: "Suspendidos" },
  { value: "INACTIVO", label: "Inactivos" },
];

function badgeColor(estatus: string): string {
  switch (estatus) {
    case "ACTIVO":      return "bg-estado-ok-bg text-estado-ok";
    case "SUSPENDIDO":  return "bg-estado-warn-bg text-estado-warn";
    case "INACTIVO":    return "bg-estado-block-bg text-estado-block";
    default:            return "bg-estado-block-bg text-estado-block";
  }
}

function vencimientoSemaforo(fin: string | null): { color: string; texto: string } {
  if (!fin) return { color: "text-fg-subtle", texto: "—" };
  const hoy = new Date();
  const venc = new Date(fin);
  const dias = Math.floor((venc.getTime() - hoy.getTime()) / (1000 * 60 * 60 * 24));
  if (dias < 0)       return { color: "text-estado-err",    texto: `Vencida (${fin})` };
  if (dias <= 60)     return { color: "text-estado-warn",  texto: `Vence ${fin} (${dias} d)` };
  return { color: "text-fg-muted", texto: fin };
}

export function LaboratoriosListPage() {
  const [q, setQ] = useState("");
  const [appliedQ, setAppliedQ] = useState("");
  const [estatus, setEstatus] = useState("");

  const query = useQuery({
    queryKey: ["laboratorios", appliedQ, estatus],
    queryFn: async () => {
      const params = new URLSearchParams({ size: "50" });
      if (appliedQ) params.set("q", appliedQ);
      if (estatus) params.set("estatus", estatus);
      const { data } = await api.get<PageResp<LaboratorioResumen>>(
        `/api/catalogos/laboratorios?${params.toString()}`,
      );
      return data;
    },
  });

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-fg">Laboratorios</h1>
          <p className="text-sm text-fg-muted">
            Catálogo de laboratorios externos y su matriz NOM/DT ↔ alcance. RN-203 / RN-204.
          </p>
        </div>
        <Link
          to="/admin/laboratorios/nuevo"
          className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-sm font-medium text-white transition hover:bg-primary-hover"
        >
          <Plus className="h-4 w-4" /> Nuevo laboratorio
        </Link>
      </header>

      <div className="flex flex-wrap gap-2 rounded-lg border bg-surface p-3 shadow-card">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-fg-faint" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && setAppliedQ(q.trim())}
            placeholder="Razón social, RFC o nombre corto…"
            className="w-full rounded border pl-8 pr-3 py-2 text-sm"
          />
        </div>
        <select
          value={estatus}
          onChange={(e) => setEstatus(e.target.value)}
          className="rounded border px-2 py-2 text-sm"
        >
          {ESTATUS_OPCIONES.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <button
          className="cursor-pointer rounded border bg-surface px-3 py-2 text-sm transition hover:bg-surface-2"
          onClick={() => setAppliedQ(q.trim())}
        >
          Buscar
        </button>
      </div>

      {query.isLoading && <p className="text-sm text-fg-subtle">Cargando…</p>}
      {query.error && (
        <div className="flex items-center gap-2 rounded border border-estado-err/25 bg-estado-err-bg p-3 text-sm text-estado-err">
          <AlertCircle className="h-4 w-4" /> Error cargando laboratorios.
        </div>
      )}

      {query.data && (
        <div className="overflow-hidden rounded-lg border bg-surface shadow-card">
          <div className="w-full overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-left text-xs uppercase tracking-wide text-fg-muted">
              <tr>
                <th className="px-3 py-2">Razón social</th>
                <th className="px-3 py-2">RFC</th>
                <th className="px-3 py-2">Acreditación EMA</th>
                <th className="px-3 py-2">Vigencia EMA</th>
                <th className="px-3 py-2">Normas</th>
                <th className="px-3 py-2">Estatus</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {query.data.content.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-6 text-center text-fg-subtle">
                    <FlaskConical className="mx-auto mb-2 h-8 w-8 text-fg-faint" />
                    Sin laboratorios registrados.
                  </td>
                </tr>
              )}
              {query.data.content.map((l) => {
                const sem = vencimientoSemaforo(l.acreditacionVigenciaFin);
                return (
                  <tr key={l.id} className="transition hover:bg-surface-2">
                    <td className="px-3 py-2">
                      <Link
                        to={`/admin/laboratorios/${l.id}`}
                        className="font-medium text-primary-strong hover:underline"
                      >
                        {l.razonSocial}
                      </Link>
                      {l.nombreCorto && (
                        <span className="ml-2 text-xs text-fg-subtle">({l.nombreCorto})</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-fg-muted">{l.rfc ?? "—"}</td>
                    <td className="px-3 py-2 text-fg-muted">{l.acreditacionEma ?? "—"}</td>
                    <td className={`px-3 py-2 ${sem.color}`}>{sem.texto}</td>
                    <td className="px-3 py-2 text-fg-muted">{l.normasAsignadas}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${badgeColor(l.estatus)}`}>
                        {l.estatus}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}
